import { z } from 'zod';
import { USER_STATUS } from '@/features/users/constants/user.constants';
import { SITE_VALUES } from '../constants/auth.constants';

export const loginPayloadSchema = z.object({
  username: z
    .string()
    .trim()
    .min(1, 'Username is required')
    .max(100, 'Username must be 100 characters or fewer'),
  password: z
    .string()
    .min(6, 'Password must be at least 6 characters')
    .max(128, 'Password must be 128 characters or fewer'),
  tenant: z.enum(SITE_VALUES),
});

export const userSummarySchema = z.object({
  id: z.coerce.number(),
  guid: z.string(),
  fullName: z.string(),
  username: z.string(),
  userTypeId: z.number(),
});

export const loginResponseSchema = z.object({
  accessToken: z.string(),
  user: userSummarySchema,
});

export const refreshResponseSchema = z.object({
  accessToken: z.string(),
});

export const userProfileSchema = z.object({
  id: z.coerce.number(),
  guid: z.string(),
  fullName: z.string(),
  username: z.string(),
  hrEmpId: z.string(),
  mobile: z.string(),
  email: z.string().nullable(),
  status: z.enum(USER_STATUS),
  userTypeName: z.string().nullable(),
  departmentName: z.string().nullable(),
  designationName: z.string().nullable(),
  companyName: z.string().nullable(),
  parentUserName: z.string().nullable(),
  thanaName: z.string().nullable(),
  districtName: z.string().nullable(),
  divisionName: z.string().nullable(),
  accountNumber: z.string().nullable(),
  nickname: z.string().nullable(),
  contactNo: z.string().nullable(),
  address: z.string().nullable(),
  activeFrom: z.string().nullable(),
  activeTo: z.string().nullable(),
});

export const changePasswordSchema = z
  .object({
    oldPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword !== data.oldPassword, {
    message: 'New password cannot be the same as old password',
    path: ['newPassword'],
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type LoginPayload = z.infer<typeof loginPayloadSchema>;
export type UserSummary = z.infer<typeof userSummarySchema>;
export type LoginResponse = z.infer<typeof loginResponseSchema>;
export type RefreshResponse = z.infer<typeof refreshResponseSchema>;
export type UserProfile = z.infer<typeof userProfileSchema>;
export type ChangePasswordPayload = z.infer<typeof changePasswordSchema>;
export type Site = (typeof SITE_VALUES)[number];
